import {
  PageInvitationEmailPreviewResponse,
  PageInvitationResponse,
} from "@shared/dtos/responses/pageInvitationResponses.dto"
import type {
  CreatePageInvitationRequest,
  PreviewPageInvitationRequest,
} from "@shared/zod/schemas/requests/pageInvitation"

import { axiosAuth } from "./generic/AxiosInterceptor/AxiosInterceptor"

export interface CreatePageInvitationOptions {
  /** Skip the interceptor's global error toast — for callers that render their own inline error. */
  suppressErrorToast?: boolean
}

/**
 * Mint a one-time `PageInvitation` link. With `delivery: "email"` it is bound
 * to `req.recipientEmail` and the backend dispatches the invitation email
 * (best-effort: a failed send is reported in `response.emailDelivery` but does
 * not roll the row back). With `delivery: "link"` an anonymous link is minted
 * and nothing is sent.
 */
export const create = async (
  practiceId: string,
  pageId: string,
  req: CreatePageInvitationRequest,
  { suppressErrorToast = false }: CreatePageInvitationOptions = {}
): Promise<PageInvitationResponse> => {
  const resp = await axiosAuth({
    url: `/practices/${practiceId}/pages/${pageId}/invitations`,
    method: "post",
    data: req,
    suppressErrorToast,
  })
  return resp.data
}

/**
 * Render the invitation email the backend would send for the given draft,
 * without persisting an invitation. Used by the portal modal to show staff
 * an inline preview of the templated email body. Returns `null` when the
 * practice has disabled the underlying template.
 */
export const previewEmail = async (
  practiceId: string,
  pageId: string,
  req: PreviewPageInvitationRequest
): Promise<PageInvitationEmailPreviewResponse | null> => {
  const resp = await axiosAuth({
    url: `/practices/${practiceId}/pages/${pageId}/invitations/preview`,
    method: "post",
    data: req,
  })
  return resp.data
}
